import { useMemo } from 'react'
import { Chain, ProtocolVersion } from 'uniswap/src/data/graphql/uniswap-data-api/__generated__/types-and-hooks'
import { useTradingApiPoolsQuery } from 'uniswap/src/data/apiClients/tradingApi/useTradingApiPoolsQuery'
import { PoolsRequest } from 'uniswap/src/data/apiClients/tradingApi/TradingApiClient'
import { UniverseChainId } from 'uniswap/src/features/chains/types'
import {
  getCurrencyAddressWithWrap,
  getSortedCurrenciesTuple,
} from 'pages/Pool/Positions/create/utils'
import { Currency } from '@uniswap/sdk-core'

export interface GetPoolsInput {
  chain: Chain | UniverseChainId
  address?: string
  poolId?: string
  token0?: string
  token1?: string
  feeTier?: string | number
}

export function useGetPoolsByTokens(input?: GetPoolsInput, enabled = true, TOKEN0?: Currency, TOKEN1?: Currency, protocolVersion?: ProtocolVersion) {
  // 构建 /api/pools API 请求参数
  const poolsRequestParams = useMemo((): PoolsRequest | undefined => {

    const sortedCurrencies = getSortedCurrenciesTuple(TOKEN0, TOKEN1)
    const token0 = getCurrencyAddressWithWrap(sortedCurrencies?.[0], protocolVersion)
    const token1 = getCurrencyAddressWithWrap(sortedCurrencies?.[1], protocolVersion)
    const variables: any = {
      token0: token0,
      token1: token1,
    }
    if (!enabled || !variables?.token0 || !variables?.token1) {
      return undefined
    }

    // 如果提供了 feeTier，添加到 variables 中
    if (input.feeTier !== undefined) {
      variables.feeTier = input.feeTier.toString()
    }

    // 根据是否有 feeTier 参数构建不同的查询
    const hasFeeTier = input.feeTier !== undefined
    
    return {
      variables,
      query: hasFeeTier 
        ? `query GetPool($token0: Bytes!, $token1: Bytes!, $feeTier: BigInt) { pools(where: { token0: $token0, token1: $token1, feeTier: $feeTier }) { id feeTier sqrtPrice liquidity tick token0 { id symbol name decimals derivedETH } token1 { id symbol name decimals derivedETH } totalValueLockedUSD } }`
        : `query GetPool($token0: Bytes!, $token1: Bytes!) { pools(where: { token0: $token0, token1: $token1 }) { id feeTier sqrtPrice liquidity tick token0 { id symbol name decimals derivedETH } token1 { id symbol name decimals derivedETH } totalValueLockedUSD } }`,
      operationName: "GetPool"
    }
  }, [enabled, input?.token0, input?.token1, input?.feeTier])

  // 使用新的 /api/pools API
  const { data: poolsApiData, isLoading: poolsApiLoading, error: poolsApiError } = useTradingApiPoolsQuery({
    params: poolsRequestParams,
    enabled: !!poolsRequestParams,
  })


  return useMemo(() => {
    // 处理加载状态
    if (poolsApiLoading) {
      return {
        data: null,
        loading: true,
        error: null,
      }
    }

    // 处理错误状态
    if (poolsApiError) {
      return {
        data: null,
        loading: false,
        error: poolsApiError,
      }
    }

    // 处理成功响应
    if (poolsApiData?.pools) {
      const formattedPools = poolsApiData.pools.map((pool) => ({
        // 映射 API 响应字段到期望的格式
        id: pool.poolId,
        address: pool.poolId,
        poolId: pool.poolId,
        feeTier: pool.fee.toString(),
        fee: pool.fee,
        sqrtPriceX96: pool.sqrtPriceX96,
        liquidity: pool.liquidity,
        tick: pool.tick,
        token0: {
          id: pool.token0,
          address: pool.token0,
          symbol: 'TOKEN0', // API 响应中没有 symbol，使用默认值
          name: 'Token 0',   // API 响应中没有 name，使用默认值
          decimals: 18,      // API 响应中没有 decimals，使用默认值
          derivedETH: '0.001', // API 响应中没有 derivedETH，使用默认值
        },
        token1: {
          id: pool.token1,
          address: pool.token1,
          symbol: 'TOKEN1', // API 响应中没有 symbol，使用默认值
          name: 'Token 1',   // API 响应中没有 name，使用默认值
          decimals: 18,      // API 响应中没有 decimals，使用默认值
          derivedETH: '0.001', // API 响应中没有 derivedETH，使用默认值
        },
        totalLiquidity: {
          value: parseFloat(pool.totalLiquidityUsd || '0'),
        },
        totalLiquidityUsd: pool.totalLiquidityUsd,
        protocolVersion: pool.protocolVersion as 'PROTOCOL_VERSION_V3',
        chainId: pool.chainId,
      }))


      return {
        data: {
          pools: formattedPools,
          totalCount: formattedPools.length,
        },
        loading: false,
        error: null,
      }
    }

    // 没有数据的情况
    return {
      data: {
        pools: [],
        totalCount: 0,
      },
      loading: false,
      error: null,
    }
  }, [poolsApiData, poolsApiLoading, poolsApiError])
}
